import React, { useState, useMemo } from 'react';
import { Box, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { AddCircleOutline as AddChannelIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ChatWithAnnotation } from '@/types/api';
import ChannelFilterBar, { FilterBarPalette } from '@/components/channels/ChannelFilterBar';
import { ALL_CHANNEL_FILTERS, ChannelFilters, filterChannels } from '@/utils/channelFilters';
import { useChatTheme } from '../ChatThemeContext';
import ChatSidebarItem from './ChatSidebarItem';
import JoinChannelDialog from './JoinChannelDialog';

interface ChatSidebarProps {
  chats: ChatWithAnnotation[];
  selectedChatId: number | null;
  onSelectChat: (chatId: number) => void;
  isLoading: boolean;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  chats,
  selectedChatId,
  onSelectChat,
  isLoading,
}) => {
  const { t } = useTranslation('pages');
  const { colors } = useChatTheme();
  // Manuscript channels are what this sidebar is for, so that is the starting
  // filter — but it is now a visible, changeable one rather than a query the
  // user cannot see (the fetch itself is unfiltered).
  const [filters, setFilters] = useState<ChannelFilters>({
    ...ALL_CHANNEL_FILTERS,
    manuscript: 'only',
  });
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);

  const palette: FilterBarPalette = useMemo(
    () => ({
      bg: colors.sidebarHeaderBg,
      fieldBg: colors.searchBg,
      fieldBorder: colors.searchBorder,
      text: colors.sidebarText,
      mutedText: colors.searchText,
      menuBg: colors.sidebarBg,
      menuHoverBg: colors.sidebarItemHover,
      menuSelectedBg: colors.sidebarItemActive,
    }),
    [colors]
  );

  const filtered = useMemo(() => filterChannels(chats, filters), [chats, filters]);

  return (
    <Box
      sx={{
        width: 360,
        minWidth: 360,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.sidebarBg,
        borderRight: `1px solid ${colors.searchBorder}`,
        borderLeft: `1px solid ${colors.searchBorder}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <ChannelFilterBar filters={filters} onChange={setFilters} palette={palette} />
        </Box>
        <Tooltip title={t('chatPreview.joinChannel.title')}>
          <IconButton
            onClick={() => setJoinDialogOpen(true)}
            sx={{ color: colors.sidebarTextMuted, mx: 0.5 }}
            size="small"
          >
            <AddChannelIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: colors.sidebarTextMuted }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: colors.sidebarTextMuted, textAlign: 'center', py: 4 }}
          >
            {t('chatPreview.sidebar.noChannels')}
          </Typography>
        ) : (
          filtered.map((chat) => (
            <ChatSidebarItem
              key={chat.id}
              chat={chat}
              isSelected={chat.id === selectedChatId}
              onClick={() => onSelectChat(chat.id)}
            />
          ))
        )}
      </Box>
      {/* Success feedback lives inside the dialog itself — a second snackbar
          here duplicated the same message. */}
      <JoinChannelDialog open={joinDialogOpen} onClose={() => setJoinDialogOpen(false)} />
    </Box>
  );
};

export default ChatSidebar;
